/**
 * Utilitário de máscara dinâmica para CPF, CNPJ tradicional e CNPJ alfanumérico.
 *
 * Regras:
 *  - Até 11 caracteres puros (somente dígitos): formata como CPF  →  000.000.000-00
 *  - De 12 a 14 caracteres puros: formata como CNPJ               →  AA.AAA.AAA/AAAA-00
 *    • As 12 primeiras posições aceitam letras (A-Z) e números.
 *    • As 2 últimas posições (DV) aceitam apenas números.
 *  - Caracteres especiais e espaços são bloqueados.
 *  - Letras são convertidas automaticamente para maiúsculas.
 *  - Limite máximo: 14 caracteres puros.
 *
 * A função `stripDocumento` retorna apenas os caracteres puros (sem máscara).
 */

/**
 * Remove todos os caracteres de formatação (pontos, traço, barra).
 * @param {string} value
 * @returns {string} Apenas caracteres alfanuméricos em caixa alta.
 */
export const stripDocumento = (value) => {
  if (!value) return "";
  return value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
};

/**
 * Aplica a máscara dinâmica de CPF ou CNPJ alfanumérico.
 *
 * @param {string} raw - Valor bruto (pode conter formatação prévia).
 * @returns {string} Valor formatado com a máscara adequada.
 */
export const maskDocumento = (raw) => {
  if (!raw) return "";

  // 1. Limpar: remove tudo que não é letra/número e converte para maiúscula
  let chars = stripDocumento(raw);

  // 2. Validar caracteres permitidos por posição e aplicar limite
  //    - CPF (até 11): somente dígitos
  //    - CNPJ (12-14): posições 0-11 alfanuméricos, posições 12-13 somente dígitos
  const cleaned = [];
  for (let i = 0; i < chars.length && cleaned.length < 14; i++) {
    const ch = chars[i];

    if (cleaned.length < 11) {
      // Zona de CPF: apenas dígitos
      if (/[0-9]/.test(ch)) {
        cleaned.push(ch);
      }
      // Se for letra, só aceita se já tivermos certeza de que é CNPJ
      // (isso será tratado abaixo no fluxo de CNPJ)
    } else if (cleaned.length < 12) {
      // 12ª posição (índice 11): alfanumérico (transição para CNPJ)
      if (/[A-Z0-9]/.test(ch)) {
        cleaned.push(ch);
      }
    } else {
      // Posições 13 e 14 (DV): apenas dígitos
      if (/[0-9]/.test(ch)) {
        cleaned.push(ch);
      }
    }
  }

  const pureValue = cleaned.join("");

  // 3. Aplicar máscara conforme tamanho
  if (pureValue.length <= 11) {
    return applyCpfMask(pureValue);
  }

  return applyCnpjMask(pureValue);
};

/**
 * Aplica a máscara dinâmica de CPF ou CNPJ alfanumérico, aceitando
 * letras nas primeiras 12 posições (para CNPJ alfanumérico).
 *
 * Esta versão é mais permissiva durante a digitação: ao detectar uma letra
 * em qualquer posição, trata automaticamente como CNPJ.
 *
 * @param {string} raw - Valor bruto (pode conter formatação prévia).
 * @returns {string} Valor formatado com a máscara adequada.
 */
export const maskDocumentoDinamico = (raw) => {
  if (!raw) return "";

  // 1. Limpar: remove tudo que não é letra/número e converte para maiúscula
  const chars = stripDocumento(raw);

  // 2. Detectar se contém letras (indica CNPJ alfanumérico)
  const hasLetters = /[A-Z]/.test(chars);

  // 3. Filtrar e limitar
  const cleaned = [];
  for (let i = 0; i < chars.length && cleaned.length < 14; i++) {
    const ch = chars[i];

    if (hasLetters) {
      // Modo CNPJ alfanumérico
      if (cleaned.length < 12) {
        // Primeiras 12 posições: alfanumérico
        if (/[A-Z0-9]/.test(ch)) {
          cleaned.push(ch);
        }
      } else {
        // DV: somente dígitos
        if (/[0-9]/.test(ch)) {
          cleaned.push(ch);
        }
      }
    } else {
      // Modo numérico (CPF ou CNPJ tradicional)
      if (/[0-9]/.test(ch)) {
        cleaned.push(ch);
      }
    }
  }

  const pureValue = cleaned.join("");

  // 4. Decidir máscara
  if (pureValue.length <= 11 && !hasLetters) {
    return applyCpfMask(pureValue);
  }

  return applyCnpjMask(pureValue);
};

/**
 * Formata como CPF: 000.000.000-00
 * @param {string} v - Valor puro (somente dígitos, até 11).
 * @returns {string}
 */
const applyCpfMask = (v) => {
  let result = "";
  for (let i = 0; i < v.length; i++) {
    if (i === 3 || i === 6) result += ".";
    if (i === 9) result += "-";
    result += v[i];
  }
  return result;
};

/**
 * Formata como CNPJ: AA.AAA.AAA/AAAA-00
 * @param {string} v - Valor puro (alfanumérico, até 14).
 * @returns {string}
 */
const applyCnpjMask = (v) => {
  let result = "";
  for (let i = 0; i < v.length; i++) {
    if (i === 2 || i === 5) result += ".";
    if (i === 8) result += "/";
    if (i === 12) result += "-";
    result += v[i];
  }
  return result;
};

